<template>
  <div class="view-gabarit">
    <h2 class="head-box-title">
      {{ params.isInternal ? $t('modal.internalShare') : userInfo.role_id === 3 ? $t('modal.clientCollaboratorShare') : $t('modal.clientShare') }}
    </h2>
    <p v-if="showSectionError" class="section-error">{{ $t('errorMessage.shareSectionError') }}</p>
    <div v-if="sharedUsers.length > 0" class="shared-container">
      <div v-for="(user, idx) in currentSharedUsers" :key="idx" class="user-container">
        <div class="user-description">
          <div class="email">{{ user.email }}</div>
          <div v-if="params.isInternal && user.role === 2" class="user-details">
            <span>{{ $t('Consultation') }}</span>
          </div>
          <div v-else-if="params.isInternal" class="user-details">
            <span>{{ $t('Modifications') }}</span>
          </div>
          <div v-else class="user-details">
            <span>{{ $t('modal.addedBy') }} {{ user.shared_by }}</span>
            <span v-if="user.status === 1">{{ $t('modal.pendingInvitation') }}</span>
            <span v-if="user.status === 2">{{ $t('modal.expiredInvitation') }}</span>
            <span v-if="user.status === 4">{{ $t('modal.resetInvitation') }} {{ formatDate(user.visited_at) }}</span>
            <span v-if="user.visited_at && user.status !== 4">{{ $t('modal.lastConnectionPrefix') }} {{ formatDate(user.visited_at) }}</span>
          </div>
        </div>
        <div class="tool-helper">
          <i
            v-if="userInfo.role_id === 1 && !params.isInternal"
            class="icon-refresh"
            :title="`Réinitialiser le compte client`"
            @click="() => resetUser(user.email)"
          ></i>
          <template v-if="!resentList.includes(user.email)">
            <span v-if="isFetching && resendEmail === user.email" class="loader"></span>
            <i v-else class="icon-link" title="Renvoyer le lien de l'étude" @click="() => resendLink(user.email)"></i>
          </template>
          <Transition name="spin">
            <i v-if="resentList.includes(user.email)" class="icon-check"></i>
          </Transition>
          <i class="icon-basket" title="Supprimer l'accès à l'étude" @click="() => deleteUser(user.email)"></i>
        </div>
      </div>
    </div>
    <div class="email-container">
      <VeeForm @submit="submitForm">
        <div class="email-thumb">
          <Field v-slot="{ handleChange, errorMessage }" name="email" rules="required|email">
            <input
              autocomplete="off"
              :class="{ 'has-error': !!errorMessage }"
              type="text"
              :value="email"
              placeholder="Email"
              @change="
                e => {
                  email = e.target.value;
                  handleChange(e);
                }
              "
            />

            <span v-if="errorMessage" class="error-caption">{{ errorMessage }}</span>
          </Field>

          <button class="study-button main" :class="{ disabled: isFetching }">
            <span v-if="!isFetching">{{ $t('add') }}</span>
            <span v-else class="loader" />
          </button>
        </div>
      </VeeForm>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatDate } from '@/libraries/formatter';
import { Field, Form as VeeForm } from 'vee-validate';

export default {
  components: {
    VeeForm,
    Field,
  },

  props: ['params'],

  data() {
    return {
      formatDate,
      isFetching: false,
      email: '',
      resendEmail: '',
      sharedUsers: [],
      resentList: [],
      showSectionError: false,
    };
  },

  computed: {
    ...mapGetters(['userInfo']),

    currentSharedUsers() {
      return this.sharedUsers.filter(item => item.is_internal === this.params.isInternal);
    },
  },

  mounted() {
    this.getData();
  },

  methods: {
    getData() {
      this.isFetching = true;
      const { id } = this.params;
      this.$store.dispatch('getShareStudy', { id }).then(response => {
        this.sharedUsers = response.data;
        this.isFetching = false;
      });
    },

    submitForm() {
      const { id, isInternal } = this.params;
      const { email } = this;
      const share = {
        is_internal: isInternal,
        email,
      };

      this.isFetching = true;

      this.$store.dispatch('postShareStudy', { id, share }).then(() => {
        this.getData();
        this.showSectionError = !isInternal && email.split('@')[1] === 'jll.com';
      });
    },

    resetUser(email) {
      this.isFetching = true;
      this.$store.dispatch('resetUserAccount', { email, id: this.params.id }).then(() => {
        this.isFetching = false;
      });
    },

    resendLink(email) {
      const { id } = this.params;
      this.resendEmail = email;
      const share = {
        is_internal: false,
        email,
      };
      this.isFetching = true;
      this.$store.dispatch('postShareStudy', { id, share }).then(() => {
        this.getData();
        this.resentList.push(email);
      });
    },

    deleteUser(email) {
      const { id } = this.params;
      this.isFetching = true;
      this.$store.dispatch('deleteShareStudyUser', { id, email }).then(() => {
        this.getData();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';
.view-gabarit {
  width: 680px;

  .shared-container {
    border-top: 1px solid #d8d8d8;
    margin-right: -30px;
    margin-bottom: -18px;
    margin-left: -30px;
    padding-right: 40px;
    padding-left: 40px;
    .user-container {
      display: flex;
      padding: 24px 0;
      box-sizing: border-box;
      & + .user-container {
        border-top: 1px solid #d8d8d8;
      }
    }
    .user-description {
      flex: 1;
      .email {
        font-weight: 600;
        color: #3c3c3b;
      }
      .user-details {
        font-size: 14px;
        color: #a5a5a5;
        span {
          display: inline-block;
          padding: 0.25em 0;
          margin-top: 0.25em;
          & + span {
            margin-left: 0.5em;
            padding-left: 0.5em;
            content: '';
            border-left: 1px solid #d8d8d8;
          }
        }
      }
    }
    .tool-helper {
      display: flex;
      justify-content: center;
      align-items: center;

      i {
        cursor: pointer;
      }

      .loader {
        width: 10px;
        height: 10px;
        margin-right: 4px;
        border: 1px solid #77797f;
        border-bottom-color: transparent;
        border-radius: 50%;
        display: inline-block;
        box-sizing: border-box;
        animation: rotation 1s linear infinite;
      }
      @keyframes rotation {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
    }
  }
  .email-container {
    background-color: #f8f7f7;
    margin-right: -30px;
    margin-bottom: -18px;
    margin-left: -30px;
    padding-right: 40px;
    padding-top: 32px;
    padding-bottom: 32px;
    padding-left: 40px;
    margin-top: 18px;

    .loader {
      width: 20px;
      height: 20px;
      margin-top: 4px;
      border: 1px solid #ffffff;
      border-bottom-color: transparent;
      border-radius: 50%;
      display: inline-block;
      box-sizing: border-box;
      animation: rotation 1s linear infinite;
    }
    @keyframes rotation {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }
  .email-thumb {
    display: flex;
    position: relative;
    padding-bottom: 16px;
    margin-bottom: -16px;
    input[type='text'] {
      flex: 1;
      margin-right: 20px;
    }
  }
}

.icon-check {
  background-image: url(../../assets/check-icon.svg);
  width: 20px;
  height: 20px;
}

.spin-enter-active,
.spin-leave-active {
  transition: all 0.2s;
}
.spin-enter, .spin-leave-to /* .fade-leave-active below version 2.1.8 */ {
  transform: rotateY(90deg);
  opacity: 0;
}
</style>
